/**
 * Hook to fetch declared outputs for seed workflows referenced by seed nodes,
 * including transitive (recursive) seed dependencies.
 *
 * @module FlowDesigner/hooks/useSeedNodeOutputs
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { WorkflowNode } from '../../../../types/workflow';
import { API_URL } from '../../../../config';
import type {
  SeedDataOutput,
  SeedOutputsResponse,
} from '../../../../hooks/useSeedWorkflowOutputs';

type SeedDependencyIdsResponse = {
  ok: boolean;
  seedWorkflowIds: string[];
};

export type TransitiveSeedOutput = {
  outputs: SeedDataOutput[];
  workflowName: string;
};

/**
 * For each seed node in the workflow, fetches the referenced seed workflow's
 * declared outputs AND transitive dependency IDs. Returns:
 * - seedOutputsByWorkflowId: Map of seedWorkflowId → SeedDataOutput[]
 * - seedTransitiveDeps: Map of seedWorkflowId → transitive seed workflow IDs
 * - transitiveSeedOutputs: Map of transitive seedWorkflowId → { outputs, workflowName }
 */
export function useSeedNodeOutputs(nodes: WorkflowNode[]) {
  // Extract unique seed workflow IDs from seed nodes
  const seedWorkflowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of nodes) {
      if (node.type === 'seed') {
        const data = node.data as Record<string, unknown> | undefined;
        const seedWorkflowId = data?.seedWorkflowId as string | undefined;
        if (seedWorkflowId) ids.add(seedWorkflowId);
      }
    }
    return Array.from(ids);
  }, [nodes]);

  // Fetch outputs for direct seed workflows. Stores the raw `SeedOutputsResponse`
  // under `['workflow', id, 'seed-outputs']` — the same shape every other
  // observer of this key writes (see useSeedWorkflowOutputs, useCallFlowNodeOutputs)
  // so a refetch from any of them doesn't clobber this cache entry with an
  // incompatible shape. Results are read back positionally (by `seedWorkflowIds[i]`)
  // below, not via a field on the response, since the raw shape has no id.
  const outputQueries = useQueries({
    queries: seedWorkflowIds.map((id) => ({
      queryKey: ['workflow', id, 'seed-outputs'],
      queryFn: async (): Promise<SeedOutputsResponse> => {
        const res = await fetch(`${API_URL}/workflows/${id}/seed-outputs`, {
          credentials: 'include',
        });
        if (!res.ok) return { ok: false, outputs: [] };
        return res.json();
      },
      staleTime: 60_000,
    })),
  });

  // Fetch transitive dependency IDs for each direct seed workflow
  const depQueries = useQueries({
    queries: seedWorkflowIds.map((id) => ({
      queryKey: ['workflow', id, 'seed-dependency-ids'],
      queryFn: async (): Promise<{ id: string; seedWorkflowIds: string[] }> => {
        const res = await fetch(`${API_URL}/workflows/${id}/seed-dependency-ids`, {
          credentials: 'include',
        });
        if (!res.ok) return { id, seedWorkflowIds: [] };
        const data: SeedDependencyIdsResponse = await res.json();
        return { id, seedWorkflowIds: data.seedWorkflowIds };
      },
      staleTime: 60_000,
    })),
  });

  // Collect transitive seed workflow IDs that need outputs fetched
  const transitiveSeedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const q of depQueries) {
      if (q.data) {
        for (const depId of q.data.seedWorkflowIds) {
          // Only fetch outputs for IDs not already in direct seeds
          if (!seedWorkflowIds.includes(depId)) {
            ids.add(depId);
          }
        }
      }
    }
    return Array.from(ids);
  }, [depQueries, seedWorkflowIds]);

  // Fetch outputs for transitive seed workflows (includes workflowName). Same
  // raw-response shape and same key-collision reasoning as outputQueries above.
  const transitiveOutputQueries = useQueries({
    queries: transitiveSeedIds.map((id) => ({
      queryKey: ['workflow', id, 'seed-outputs'],
      queryFn: async (): Promise<SeedOutputsResponse> => {
        const res = await fetch(`${API_URL}/workflows/${id}/seed-outputs`, {
          credentials: 'include',
        });
        if (!res.ok) return { ok: false, outputs: [] };
        return res.json();
      },
      staleTime: 60_000,
    })),
  });

  const seedOutputsByWorkflowId = useMemo(() => {
    const map = new Map<string, SeedDataOutput[]>();
    outputQueries.forEach((q, i) => {
      if (q.data) {
        map.set(seedWorkflowIds[i], q.data.outputs);
      }
    });
    return map;
  }, [outputQueries, seedWorkflowIds]);

  const seedTransitiveDeps = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const q of depQueries) {
      if (q.data) {
        // `q.data.id` is real here — this reads `depQueries`, keyed
        // `['workflow', id, 'seed-dependency-ids']`, a single-writer key
        // distinct from the shared `seed-outputs` key above whose raw
        // response has no `id` field.
        map.set(q.data.id, q.data.seedWorkflowIds);
      }
    }
    return map;
  }, [depQueries]);

  const transitiveSeedOutputs = useMemo(() => {
    const map = new Map<string, TransitiveSeedOutput>();
    transitiveOutputQueries.forEach((q, i) => {
      if (q.data) {
        map.set(transitiveSeedIds[i], {
          outputs: q.data.outputs,
          workflowName: q.data.workflowName ?? '',
        });
      }
    });
    return map;
  }, [transitiveOutputQueries, transitiveSeedIds]);

  const isLoading = outputQueries.some((q) => q.isLoading);

  return { seedOutputsByWorkflowId, seedTransitiveDeps, transitiveSeedOutputs, isLoading };
}
